import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, File, Folder, FolderOpen } from 'lucide-react';
import type { VaultTreeNode } from '@cortex/shared';
import { useTree, qk } from '../../hooks/useVault';
import { useUi } from '../../store/ui';
import { Api } from '../../lib/api';
import { TypeDot } from '../ui/TypeDot';
import { NewNoteDialog } from '../dialogs/NewNoteDialog';

interface Menu {
  x: number;
  y: number;
  node: VaultTreeNode;
}

export function VaultTree() {
  const tree = useTree();
  const [menu, setMenu] = useState<Menu | null>(null);
  const [newIn, setNewIn] = useState<string | null>(null);
  if (!tree.data) return <div className="tree" style={{ color: 'var(--text-faint)', padding: 8 }}>{tree.isError ? 'Vault unavailable' : ''}</div>;
  return (
    <div className="tree" onClick={() => setMenu(null)}>
      {tree.data.map((n) => (
        <TreeNode key={n.path} node={n} depth={0} onMenu={(e, node) => (e.preventDefault(), setMenu({ x: e.clientX, y: e.clientY, node }))} />
      ))}
      {menu && <ContextMenu menu={menu} onClose={() => setMenu(null)} onNew={(dir) => setNewIn(dir)} />}
      {newIn !== null && <NewNoteDialog folder={newIn} onClose={() => setNewIn(null)} />}
    </div>
  );
}

function TreeNode({ node, depth, onMenu }: { node: VaultTreeNode; depth: number; onMenu: (e: React.MouseEvent, n: VaultTreeNode) => void }) {
  const collapsed = useUi((s) => s.collapsedDirs[node.path] ?? depth > 0);
  const toggleDir = useUi((s) => s.toggleDir);
  const openNote = useUi((s) => s.openNote);
  const activeId = useUi((s) => s.panes.find((p) => p.id === s.activePaneId)?.noteId);
  const pad = { paddingLeft: 6 + depth * 12 };

  if (node.kind === 'dir') {
    return (
      <>
        <div className="tree-row" style={pad} onClick={() => toggleDir(node.path)} onContextMenu={(e) => onMenu(e, node)}>
          <span className="chev">{collapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}</span>
          {collapsed ? <Folder size={13} /> : <FolderOpen size={13} />}
          <span className="name">{node.name}</span>
          <span className="hint mono" style={{ marginLeft: 'auto', color: 'var(--text-faint)', fontSize: 10 }}>{countNotes(node)}</span>
        </div>
        {!collapsed && node.children?.map((c) => <TreeNode key={c.path} node={c} depth={depth + 1} onMenu={onMenu} />)}
      </>
    );
  }
  if (node.kind === 'note') {
    return (
      <div
        className={`tree-row${node.noteId && node.noteId === activeId ? ' active' : ''}`}
        style={pad}
        title={node.path}
        onClick={(e) => node.noteId && openNote(node.noteId, { split: e.ctrlKey || e.metaKey })}
        onContextMenu={(e) => onMenu(e, node)}
      >
        <span className="chev" />
        <TypeDot type={node.type ?? 'note'} />
        <span className="name">{node.title ?? node.name.replace(/\.md$/, '')}</span>
      </div>
    );
  }
  return (
    <div className="tree-row file" style={pad} title={node.path} onContextMenu={(e) => onMenu(e, node)}>
      <span className="chev" />
      <File size={12} />
      <span className="name">{node.name}</span>
    </div>
  );
}

function countNotes(n: VaultTreeNode): number {
  return (n.children ?? []).reduce((a, c) => a + (c.kind === 'note' ? 1 : c.kind === 'dir' ? countNotes(c) : 0), 0);
}

function ContextMenu({ menu, onClose, onNew }: { menu: Menu; onClose: () => void; onNew: (dir: string) => void }) {
  const qc = useQueryClient();
  const ui = useUi();
  const { node } = menu;
  const dir = node.kind === 'dir' ? node.path : node.path.includes('/') ? node.path.slice(0, node.path.lastIndexOf('/')) : '';
  const run = async (fn: () => Promise<void>) => {
    try {
      await fn();
      qc.invalidateQueries({ queryKey: qk.tree });
    } catch (err) {
      ui.toast((err as Error).message, 'crit');
    } finally {
      onClose();
    }
  };
  return (
    <div className="ctx-menu panel" style={{ left: menu.x, top: menu.y }} onMouseLeave={onClose}>
      <button onClick={() => (onNew(dir), onClose())}>New note in {dir || 'vault root'}</button>
      {node.kind === 'note' && node.noteId && (
        <>
          <button onClick={() => (ui.openNote(node.noteId!, { split: true }), onClose())}>Open in split</button>
          <button
            onClick={() =>
              run(async () => {
                const to = window.prompt('New path (vault-relative, .md):', node.path);
                if (!to || to === node.path) return;
                const r = await Api.notes.rename(node.noteId!, to);
                ui.dropDraft(node.noteId!);
                ui.openNote(r.id);
                ui.toast(`Renamed to ${r.path}`);
              })
            }
          >
            Rename / move…
          </button>
          <button
            onClick={() => navigator.clipboard.writeText(`[[${node.title ?? node.name.replace(/\.md$/, '')}]]`).then(() => (ui.toast('Wikilink copied'), onClose()))}
          >
            Copy wikilink
          </button>
          <button
            className="danger"
            onClick={() =>
              run(async () => {
                if (!window.confirm(`Move "${node.path}" to trash?`)) return;
                const r = await Api.notes.remove(node.noteId!);
                ui.dropDraft(node.noteId!);
                for (const p of ui.panes) if (p.noteId === node.noteId) ui.closePane(p.id);
                ui.toast(`Moved to ${r.trashedTo}`, 'warn');
              })
            }
          >
            Move to trash
          </button>
        </>
      )}
    </div>
  );
}
